import { toaster } from "@/components/ui/toaster";
import { getPurchaseOrderIntentList } from "@/services/purchase-order-intent.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import {
  PurchaseOrderIntentListResponse,
  purchaseOrderIntentOpenStatusIds,
} from "./purchase-order-intent.schema";

interface FetchPurchaseOrderIntentHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<PurchaseOrderIntentListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const getDefaultPurchaseOrderIntentFilters = () => ({
  Estados: purchaseOrderIntentOpenStatusIds,
  "P.Pagina": 1,
  "P.Records": 10,
});

export const fetchPurchaseOrderIntentListHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchPurchaseOrderIntentHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getPurchaseOrderIntentList({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setList(response);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      const errorObj = error as AxiosError;
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las intenciones de compra.",
        ),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitFiltersHandlerProps) => {
  const fechaSolicitud = event?.fechaSolicitud
    ? dayjs(event.fechaSolicitud).format("YYYY-MM-DD")
    : undefined;

  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v: any) => !Number.isNaN(v))
    : undefined;

  const cuentaRaw = typeof event?.cuenta === "string" ? event.cuenta.trim() : event?.cuenta;
  const cuenta =
    cuentaRaw === undefined || cuentaRaw === null || cuentaRaw === "" ? undefined : Number(cuentaRaw);

  const nextFilters = {
    Cuenta: Number.isNaN(cuenta as any) ? undefined : cuenta,
    FechaSolicitud: fechaSolicitud,
    ZonaComercial:
      typeof event?.zonaComercial === "string" && event.zonaComercial.trim() !== ""
        ? event.zonaComercial
        : undefined,
    TipoInsumo:
      Array.isArray(event?.tipoInsumo) && event.tipoInsumo?.[0]
        ? Number(event.tipoInsumo?.[0])
        : undefined,
    Estados: estados?.length ? estados : undefined,
    "P.Pagina": 1,
    "P.Records": 10,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearFiltersHandlerProps) => {
  setValue("cuenta", "");
  setValue("fechaSolicitud", undefined);
  setValue("zonaComercial", "");
  setValue("tipoInsumo", []);
  setValue("estados", []);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);

  const nextFilters = {
    "P.Pagina": 1,
    "P.Records": 10,
  };
  setCurrentFilters(nextFilters);

  await fetchList(nextFilters);
};
